import React, { useEffect, useState } from 'react';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';

const CA_PROVINCES = ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'];
const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];

export function regionOptionsFor(country) {
  return country === 'United States' ? US_STATES : CA_PROVINCES;
}

const TABS = ['Company Profile', 'Tax Information', 'Authorities & Compliance', 'Invoicing'];

export default function Settings() {
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api('/settings').then(setForm).catch(() => setForm({}));
  }, []);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    try {
      const updated = await api('/settings', {
        method: 'PATCH',
        body: {
          companyName: form.company_name, logoUrl: form.logo_url, brandColor: form.brand_color,
          invoicePrefix: form.invoice_prefix, invoiceNextNumber: form.invoice_next_number,
          country: form.country, provinceState: form.province_state,
          businessNumber: form.business_number, gstNumber: form.gst_number, qstNumber: form.qst_number,
          pstNumber: form.pst_number, hstNumber: form.hst_number, ein: form.ein, usStateTaxId: form.us_state_tax_id,
          usdotNumber: form.usdot_number, mcNumber: form.mc_number, mcAuthorityType: form.mc_authority_type,
          iftaAccount: form.ifta_account, irpAccount: form.irp_account, nyHutNumber: form.ny_hut_number,
          kyuNumber: form.kyu_number, nmWeightDistanceNumber: form.nm_weight_distance_number,
          orWeightMileNumber: form.or_weight_mile_number, ucrNumber: form.ucr_number,
          boc3ProcessAgent: form.boc3_process_agent, scacCode: form.scac_code, nscNumber: form.nsc_number, cvorNumber: form.cvor_number,
          brokerBondType: form.broker_bond_type, brokerBondNumber: form.broker_bond_number, brokerBondAmount: form.broker_bond_amount
        }
      });
      setForm(updated);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  if (!form) return <div className="empty">Loading...</div>;

  const country = form.country || 'Canada';
  const isUS = country === 'United States';
  const regionLabel = isUS ? 'State' : 'Province';
  const isBroker = form.account_type === 'broker' || form.account_type === 'both';
  const isCarrier = form.account_type === 'carrier' || form.account_type === 'both';

  return (
    <div>
      <PageHeader
        icon={Icon.settings}
        title="Settings"
        subtitle="Your company profile — branding, tax registration, and operating authorities"
        color={SECTION_COLOR.utilities}
      />

      <div className="form-tabs" style={{ marginBottom: 20 }}>
        {TABS.map((t, i) => (
          <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
        ))}
      </div>

      <div style={{ maxWidth: 720 }}>
        {tab === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label>Company name<br/><input value={form.company_name || ''} onChange={e => update('company_name', e.target.value)} /></label>
            <label>Logo URL<br/><input value={form.logo_url || ''} onChange={e => update('logo_url', e.target.value)} placeholder="https://..." /></label>
            <label>Brand color<br/><input type="color" value={form.brand_color || '#F2A93B'} onChange={e => update('brand_color', e.target.value)} style={{ width: 80, padding: 4 }} /></label>

            <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginTop: 10 }}>COMPANY REGISTRATION</div>
            <label>Country your company is registered in<br/>
              <select value={country} onChange={e => { update('country', e.target.value); update('province_state', ''); }}>
                <option>Canada</option>
                <option>United States</option>
              </select>
            </label>
            <label>{regionLabel} of registration<br/>
              <select value={form.province_state || ''} onChange={e => update('province_state', e.target.value)}>
                <option value="">—</option>
                {regionOptionsFor(country).map(r => <option key={r}>{r}</option>)}
              </select>
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                This decides which employment rules show on every employee's Payroll &amp; Tax tab — SIN/TD1/CPP-EI
                for Canada, or SSN/W-4/state withholding for the US.
              </span>
            </label>
          </div>
        )}

        {tab === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!isUS ? (
              <>
                <label>Business Number (BN)<br/><input value={form.business_number || ''} onChange={e => update('business_number', e.target.value)} placeholder="9 digits from CRA" /></label>
                <label>GST Number<br/><input value={form.gst_number || ''} onChange={e => update('gst_number', e.target.value)} /></label>
                {form.province_state === 'Quebec' && (
                  <label>QST Number<br/><input value={form.qst_number || ''} onChange={e => update('qst_number', e.target.value)} /></label>
                )}
                <label>HST Number (if your province uses HST)<br/><input value={form.hst_number || ''} onChange={e => update('hst_number', e.target.value)} /></label>
                <label>PST Number (if your province charges separate PST)<br/><input value={form.pst_number || ''} onChange={e => update('pst_number', e.target.value)} /></label>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Only fill in what actually applies to your province — most provinces use one or the other, not all of these.</span>
              </>
            ) : (
              <>
                <label>EIN (Employer Identification Number)<br/><input value={form.ein || ''} onChange={e => update('ein', e.target.value)} placeholder="from the IRS" /></label>
                <label>State Tax ID<br/><input value={form.us_state_tax_id || ''} onChange={e => update('us_state_tax_id', e.target.value)} /></label>
              </>
            )}
          </div>
        )}

        {tab === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isCarrier && (
              <>
                <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)' }}>CARRIER AUTHORITY</div>
                <label>USDOT Number<br/><input value={form.usdot_number || ''} onChange={e => update('usdot_number', e.target.value)} /></label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <label>MC Number<br/><input value={form.mc_number || ''} onChange={e => update('mc_number', e.target.value)} /></label>
                  <label>MC Authority Type<br/>
                    <select value={form.mc_authority_type || ''} onChange={e => update('mc_authority_type', e.target.value)}>
                      <option value="">—</option><option>Common</option><option>Contract</option><option>Broker</option>
                    </select>
                  </label>
                </div>

                <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginTop: 8 }}>FUEL &amp; REGISTRATION</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <label>IFTA Account #<br/><input value={form.ifta_account || ''} onChange={e => update('ifta_account', e.target.value)} placeholder="International Fuel Tax Agreement" /></label>
                  <label>IRP Account #<br/><input value={form.irp_account || ''} onChange={e => update('irp_account', e.target.value)} placeholder="apportioned plates" /></label>
                </div>

                <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginTop: 8 }}>STATE HIGHWAY USE TAXES</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                  <label>NY HUT #<br/><input value={form.ny_hut_number || ''} onChange={e => update('ny_hut_number', e.target.value)} /></label>
                  <label>KYU # (Kentucky)<br/><input value={form.kyu_number || ''} onChange={e => update('kyu_number', e.target.value)} /></label>
                  <label>NM Weight-Distance #<br/><input value={form.nm_weight_distance_number || ''} onChange={e => update('nm_weight_distance_number', e.target.value)} /></label>
                </div>
                <label style={{ maxWidth: 240 }}>OR Weight-Mile #<br/><input value={form.or_weight_mile_number || ''} onChange={e => update('or_weight_mile_number', e.target.value)} /></label>

                <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginTop: 8 }}>OTHER COMPLIANCE</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <label>UCR Number<br/><input value={form.ucr_number || ''} onChange={e => update('ucr_number', e.target.value)} placeholder="Unified Carrier Registration" /></label>
                  <label>SCAC Code<br/><input value={form.scac_code || ''} onChange={e => update('scac_code', e.target.value)} maxLength={4} /></label>
                </div>
                <label>BOC-3 Process Agent<br/><input value={form.boc3_process_agent || ''} onChange={e => update('boc3_process_agent', e.target.value)} /></label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <label>National Safety Code # (Canada)<br/><input value={form.nsc_number || ''} onChange={e => update('nsc_number', e.target.value)} /></label>
                  <label>CVOR # (Ontario)<br/><input value={form.cvor_number || ''} onChange={e => update('cvor_number', e.target.value)} /></label>
                </div>
              </>
            )}

            {isBroker && (
              <>
                <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginTop: isCarrier ? 18 : 0 }}>BROKER AUTHORITY</div>
                {isCarrier && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: -8 }}>Brokers also need their own MC Number, entered above with Authority Type set to "Broker".</div>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <label>Bond / Trust Fund Type<br/>
                    <select value={form.broker_bond_type || ''} onChange={e => update('broker_bond_type', e.target.value)}>
                      <option value="">—</option>
                      <option>Surety Bond (BMC-84)</option>
                      <option>Trust Fund (BMC-85)</option>
                    </select>
                  </label>
                  <label>Bond Amount ($)<br/><input type="number" value={form.broker_bond_amount || ''} onChange={e => update('broker_bond_amount', e.target.value)} placeholder="FMCSA minimum is $75,000" /></label>
                </div>
                <label style={{ maxWidth: 320 }}>Bond / Trust Fund Number<br/><input value={form.broker_bond_number || ''} onChange={e => update('broker_bond_number', e.target.value)} /></label>
              </>
            )}
          </div>
        )}

        {tab === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label>
              Invoice prefix (optional — e.g. "PF" makes invoices read PF-1001)
              <input value={form.invoice_prefix || ''} onChange={e => update('invoice_prefix', e.target.value)} placeholder="e.g. PF" />
            </label>
            <label>
              Next invoice number
              <input type="number" value={form.invoice_next_number || 1001} onChange={e => update('invoice_next_number', Number(e.target.value))} />
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                Change this if you're migrating from another system and want to continue your existing numbering
                instead of starting over at 1001. This only affects invoices created from now on.
              </span>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <button className="primary-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</button>
          {saved && <span style={{ color: 'var(--green)', fontSize: 13 }}>Saved.</span>}
        </div>
      </div>
    </div>
  );
}
